/**
 * WebSocket protocol — the full envelope catalog.
 *
 * Source of truth: `docs/prd/07-api-contract.md` §5 (envelope + frame catalog),
 * cross-checked against `05-realtime-infra.md` (ordering/idempotency runtime).
 *
 * Wire rules (07 §1, §5):
 * - One multiplexed JSON-text socket per tab → `UserHub` DO → `ChatRoom` DOs.
 * - Every frame carries the common envelope (`t`, `v`, `id`, `ts`, optional `cid`).
 * - Frame ids and `client_msg_id` are ULIDs (lexicographically sortable).
 * - `ts` is ADVISORY ONLY — clients render by the server-assigned `seq`, never `ts`.
 * - Binary payloads (`ciphertext`, `ratchet_header`) are base64url strings.
 * - A single text frame must be ≤ 256 KB (see `WS_FRAME_MAX_BYTES` in errors.ts).
 */

import type { WsErrorCode } from './errors';

/** Current WS protocol version. All frames carry `"v": 1`. */
export const WS_PROTOCOL_VERSION = 1 as const;
export type WsProtocolVersion = typeof WS_PROTOCOL_VERSION;

/** A base64url-encoded binary blob (ciphertext, ratchet header, keys). */
export type Base64Url = string;

/** ULID string (frame id, `client_msg_id`, resume token suffix). */
export type Ulid = string;

/** UUID v4 / v7 string (user, conversation, message, media ids). */
export type Uuid = string;

/** Server epoch-millisecond timestamp. */
export type EpochMs = number;

/**
 * Opaque sender content hint (07 §5.2). Used only for client notification
 * fallback — never server logic. `system` is reserved for client-rendered
 * control messages (e.g. "X was added") and tombstones.
 */
export const WS_CONTENT_TYPES = ['text', 'image', 'file', 'audio', 'video', 'system'] as const;
export type WsContentType = (typeof WS_CONTENT_TYPES)[number];

/** Presence state carried by `presence.update` (07 §5.2). */
export type PresenceState = 'online' | 'offline';

/** Roster mutation kind carried by `conv.member_changed` (07 §5.2). */
export type MemberChangeAction = 'added' | 'removed' | 'role' | 'left';

/* -------------------------------------------------------------------------- */
/*  Common envelope (§5.1)                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Fields present on EVERY frame. Concrete frame types intersect this with their
 * type-specific payload. `cid` is present on conversation-scoped frames and
 * omitted on account-level frames (`conn.ready`, `auth.*`, `presence.*`,
 * `sync.request`, `ping`/`pong`).
 */
export interface WsEnvelope<T extends WsFrameType> {
  /** Type discriminator (the frame catalog below). */
  t: T;
  /** Protocol version — always `1`. */
  v: WsProtocolVersion;
  /** ULID frame id; correlates acks/errors to the originating frame. */
  id: Ulid;
  /** Sender wall-clock ms — ADVISORY ONLY, never used for ordering. */
  ts: EpochMs;
  /** Conversation scope; omitted for account-level frames. */
  cid?: Uuid;
}

/* -------------------------------------------------------------------------- */
/*  Frame type catalog (§5.2)                                                  */
/* -------------------------------------------------------------------------- */

/** Every `t` discriminator in the protocol. */
export const WS_FRAME_TYPES = [
  'conn.ready',
  'auth.refresh',
  'auth.ok',
  'sync.request',
  'sync.batch',
  'msg.send',
  'msg.ack',
  'msg.new',
  'msg.delivered',
  'msg.read',
  'typing.start',
  'typing.stop',
  'presence.subscribe',
  'presence.update',
  'conv.updated',
  'conv.member_changed',
  'error',
  'ping',
  'pong',
] as const;

export type WsFrameType = (typeof WS_FRAME_TYPES)[number];

/* -------------------------------------------------------------------------- */
/*  Shared sub-shapes                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The canonical "stored message" shape carried by `msg.new` and (in batches) by
 * `sync.batch`. The `ciphertext` is the blob addressed to the caller's device.
 */
export interface WsStoredMessage {
  seq: number;
  sender_id: Uuid;
  client_msg_id: Ulid;
  content_type: WsContentType;
  ciphertext: Base64Url;
  ratchet_header: Base64Url;
  /** Present when the message references an R2 media object. */
  media_id?: Uuid;
  /** `seq` of the message this one replies to, if any. */
  reply_to_seq?: number;
  server_ts: EpochMs;
}

/** One target in a `sync.request` (07 §5.4). */
export interface WsSyncTarget {
  cid: Uuid;
  /** Highest `seq` the client already holds for this conversation. */
  since_seq: number;
}

/* -------------------------------------------------------------------------- */
/*  Client → Server frames (C→S)                                              */
/* -------------------------------------------------------------------------- */

/** Re-authenticate a live socket with a fresh WS ticket (account-level). */
export interface AuthRefreshFrame extends WsEnvelope<'auth.refresh'> {
  ticket: string;
}

/** Catch-up request across one or more conversations (account-level). */
export interface SyncRequestFrame extends WsEnvelope<'sync.request'> {
  targets: WsSyncTarget[];
}

/** New outbound message (ciphertext). Answered by exactly one `msg.ack`. */
export interface MsgSendFrame extends WsEnvelope<'msg.send'> {
  cid: Uuid;
  /** ULID idempotency key; a retry with the same value re-yields the same `seq`. */
  client_msg_id: Ulid;
  content_type: WsContentType;
  ciphertext: Base64Url;
  ratchet_header: Base64Url;
  /** Set when the body references an R2 blob uploaded via presign-upload. */
  media_id?: Uuid;
  /** `seq` of the message being replied to, if any. */
  reply_to_seq?: number;
}

/** Cursor-style delivered receipt (C→S form). Monotonic & idempotent. */
export interface MsgDeliveredClientFrame extends WsEnvelope<'msg.delivered'> {
  cid: Uuid;
  up_to_seq: number;
}

/** Cursor-style read receipt (C→S form). Monotonic & idempotent. */
export interface MsgReadClientFrame extends WsEnvelope<'msg.read'> {
  cid: Uuid;
  up_to_seq: number;
}

/** Typing started (C→S form; server adds `user_id` on re-broadcast). */
export interface TypingStartClientFrame extends WsEnvelope<'typing.start'> {
  cid: Uuid;
}

/** Typing stopped (C→S form; server adds `user_id` on re-broadcast). */
export interface TypingStopClientFrame extends WsEnvelope<'typing.stop'> {
  cid: Uuid;
}

/** Subscribe to presence updates for a set of users (account-level). */
export interface PresenceSubscribeFrame extends WsEnvelope<'presence.subscribe'> {
  user_ids: Uuid[];
}

/** Heartbeat ping (C→S). Auto-answered with `pong`; does not wake the DO. */
export type PingFrame = WsEnvelope<'ping'>;

/* -------------------------------------------------------------------------- */
/*  Server → Client frames (S→C)                                              */
/* -------------------------------------------------------------------------- */

/** Handshake complete (account-level, first frame after connect). */
export interface ConnReadyFrame extends WsEnvelope<'conn.ready'> {
  server_time: EpochMs;
  /** Token to resume/reattach this logical session after a transient drop. */
  resume_token: string;
  user_id: Uuid;
}

/** Re-auth accepted (account-level). */
export interface AuthOkFrame extends WsEnvelope<'auth.ok'> {
  auth_expires_at_ms: EpochMs;
}

/** Ordered backfill page answering a `sync.request` target. */
export interface SyncBatchFrame extends WsEnvelope<'sync.batch'> {
  cid: Uuid;
  /** `msg.new`-shaped objects in ascending `seq` order. */
  messages: WsStoredMessage[];
  has_more: boolean;
  /** Pass back as the next `since_seq` to continue paging this conversation. */
  next_since_seq: number;
}

/** Server persisted the sender's message (S→C). Correlates via `client_msg_id`. */
export interface MsgAckFrame extends WsEnvelope<'msg.ack'> {
  cid: Uuid;
  client_msg_id: Ulid;
  /** The durable, gap-free per-conversation sequence number assigned by the DO. */
  seq: number;
  server_ts: EpochMs;
}

/** Fanout of a stored message to recipients (S→C). Sender gets `msg.ack` instead. */
export interface MsgNewFrame extends WsEnvelope<'msg.new'>, WsStoredMessage {
  cid: Uuid;
}

/** Delivered receipt re-broadcast (S→C form; carries the recipient `user_id`). */
export interface MsgDeliveredServerFrame extends WsEnvelope<'msg.delivered'> {
  cid: Uuid;
  up_to_seq: number;
  user_id: Uuid;
}

/** Read receipt re-broadcast (S→C form; carries the recipient `user_id`). */
export interface MsgReadServerFrame extends WsEnvelope<'msg.read'> {
  cid: Uuid;
  up_to_seq: number;
  user_id: Uuid;
}

/** Typing started re-broadcast (S→C form; carries the typer's `user_id`). */
export interface TypingStartServerFrame extends WsEnvelope<'typing.start'> {
  cid: Uuid;
  user_id: Uuid;
}

/** Typing stopped re-broadcast (S→C form; carries the typer's `user_id`). */
export interface TypingStopServerFrame extends WsEnvelope<'typing.stop'> {
  cid: Uuid;
  user_id: Uuid;
}

/** Presence change for a watched user (S→C, account-level). */
export interface PresenceUpdateFrame extends WsEnvelope<'presence.update'> {
  user_id: Uuid;
  state: PresenceState;
  /** Last-seen ms; `null` when hidden by the user's privacy setting. */
  last_seen_at_ms?: EpochMs | null;
}

/** Group metadata changed (S→C). */
export interface ConvUpdatedFrame extends WsEnvelope<'conv.updated'> {
  cid: Uuid;
  title?: string;
  avatar_url?: string | null;
  by_user_id: Uuid;
}

/** Roster change (S→C). `role` present when `action === 'role'`. */
export interface ConvMemberChangedFrame extends WsEnvelope<'conv.member_changed'> {
  cid: Uuid;
  action: MemberChangeAction;
  user_id: Uuid;
  role?: 'admin' | 'member';
  by_user_id: Uuid;
}

/** Per-frame, recoverable error referencing the offending frame's `id` (S→C). */
export interface ErrorFrame extends WsEnvelope<'error'> {
  /** `id` of the frame this error responds to. */
  ref_id: Ulid;
  code: WsErrorCode;
  message: string;
  /** Whether re-sending the same frame is safe (catalog-sourced). */
  retryable: boolean;
}

/** Heartbeat pong (S→C). */
export type PongFrame = WsEnvelope<'pong'>;

/* -------------------------------------------------------------------------- */
/*  Directional unions + full catalog                                         */
/* -------------------------------------------------------------------------- */

/**
 * `msg.delivered` / `msg.read` / `typing.*` are bidirectional with DIFFERENT
 * shapes per direction (server forms add `user_id`). These aliases pick the
 * right shape for a given side.
 */
export type MsgDeliveredFrame = MsgDeliveredClientFrame | MsgDeliveredServerFrame;
export type MsgReadFrame = MsgReadClientFrame | MsgReadServerFrame;
export type TypingStartFrame = TypingStartClientFrame | TypingStartServerFrame;
export type TypingStopFrame = TypingStopClientFrame | TypingStopServerFrame;

/** Every frame a client may SEND (C→S). */
export type ClientToServerFrame =
  | AuthRefreshFrame
  | SyncRequestFrame
  | MsgSendFrame
  | MsgDeliveredClientFrame
  | MsgReadClientFrame
  | TypingStartClientFrame
  | TypingStopClientFrame
  | PresenceSubscribeFrame
  | PingFrame;

/** Every frame a server may SEND (S→C). */
export type ServerToClientFrame =
  | ConnReadyFrame
  | AuthOkFrame
  | SyncBatchFrame
  | MsgAckFrame
  | MsgNewFrame
  | MsgDeliveredServerFrame
  | MsgReadServerFrame
  | TypingStartServerFrame
  | TypingStopServerFrame
  | PresenceUpdateFrame
  | ConvUpdatedFrame
  | ConvMemberChangedFrame
  | ErrorFrame
  | PongFrame;

/**
 * The complete discriminated union of every WS frame, keyed on `t`.
 * Use this for an exhaustive router; narrow per direction with the aliases above.
 */
export type WsFrame = ClientToServerFrame | ServerToClientFrame;

/**
 * Map from each frame `t` to its concrete frame type. For bidirectional types
 * this resolves to the directional UNION (both shapes), matching `WsFrame`.
 */
export interface WsFrameByType {
  'conn.ready': ConnReadyFrame;
  'auth.refresh': AuthRefreshFrame;
  'auth.ok': AuthOkFrame;
  'sync.request': SyncRequestFrame;
  'sync.batch': SyncBatchFrame;
  'msg.send': MsgSendFrame;
  'msg.ack': MsgAckFrame;
  'msg.new': MsgNewFrame;
  'msg.delivered': MsgDeliveredFrame;
  'msg.read': MsgReadFrame;
  'typing.start': TypingStartFrame;
  'typing.stop': TypingStopFrame;
  'presence.subscribe': PresenceSubscribeFrame;
  'presence.update': PresenceUpdateFrame;
  'conv.updated': ConvUpdatedFrame;
  'conv.member_changed': ConvMemberChangedFrame;
  'error': ErrorFrame;
  'ping': PingFrame;
  'pong': PongFrame;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Type guard narrowing any frame to a specific `t`. */
export function isFrameType<T extends WsFrameType>(
  frame: WsFrame,
  t: T,
): frame is Extract<WsFrame, { t: T }> {
  return frame.t === t;
}

/**
 * Construct the common envelope for a frame. Track code spreads this then adds
 * the type-specific payload, keeping `v` and the field order uniform.
 */
export function makeEnvelope<T extends WsFrameType>(args: {
  t: T;
  id: Ulid;
  ts?: EpochMs;
  cid?: Uuid;
}): WsEnvelope<T> {
  const env: WsEnvelope<T> = {
    t: args.t,
    v: WS_PROTOCOL_VERSION,
    id: args.id,
    ts: args.ts ?? Date.now(),
  };
  if (args.cid !== undefined) env.cid = args.cid;
  return env;
}
